import { ContextSourcesFilters, EditContextSourceDialog, FilesUploadDialog, RepositoriesUploadDialog, WikiPagesUploadDialog, getSourceCodeIcon, isMarkdownContextFile } from '@/components/context-sources'
import { ArchiveIcon, MenuBookIcon } from '@/components/icons'
import { ContextHeader } from '@/components/projects/context'
import { SortableTableHead } from '@/components/projects/SortableTableHead'
import { cn } from '@/lib/utils'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import {
  useAuth,
  useContextSources,
  useDebounce,
  useDeleteContextSource,
  useDownloadContextSource,
  usePagination,
} from '@/hooks'
import { ContextSourceKinds, ContextSourceTypes, MAX_INLINE_EDITABLE_CONTENT_SIZE_BYTES, Permissions } from '@/lib'
import type { ContextSourceListItem } from '@/types'
import { CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS, type ContextSourceSortColumn } from '@/types/contextSource'
import {
  Download,
  FileText,
  Image as ImageIcon,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const FILTER_KEYS = ['sortBy', 'sort'] as const

const sourceTypeIcon = (source: ContextSourceListItem) => {
  if (source.sourceType === ContextSourceTypes.Image) return { icon: <ImageIcon className="h-4 w-4 text-accent-soft" aria-hidden="true" />, label: 'Image' }
  if (source.sourceType === ContextSourceTypes.SourceCode) {
    const { Icon, label, isFallback } = getSourceCodeIcon(source.connectorType)
    return { icon: <Icon className={cn('h-4 w-4', isFallback && 'text-accent-soft')} aria-hidden="true" />, label }
  }
  if (source.sourceType === ContextSourceTypes.Archive) return { icon: <ArchiveIcon className="h-4 w-4 text-accent-soft" aria-hidden="true" />, label: 'Archive' }
  return { icon: <FileText className="h-4 w-4 text-accent-soft" aria-hidden="true" />, label: 'Document' }
}

function SourceIcon({ source }: { source: ContextSourceListItem }) {
  const { icon, label } = sourceTypeIcon(source)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span role="img" aria-label={label} className="inline-flex h-4 w-4 items-center justify-center">{icon}</span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const formatDateTime = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString()
}


export function ContextPage() {
  const { projectId } = useParams()
  const resolvedProjectId = projectId ?? ''
  const { hasPermission } = useAuth(resolvedProjectId || undefined)
  const canCreate = hasPermission(Permissions.Project.Context.Create, resolvedProjectId)
  const canDelete = hasPermission(Permissions.Project.Context.Delete, resolvedProjectId)
  const canEdit = hasPermission(Permissions.Project.Context.Update, resolvedProjectId)

  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()

  const [search, setSearch] = useState('')

  const debouncedSearch = useDebounce(search)

  const [searchParams, setSearchParams] = useSearchParams()
  const storageKey = `context-sources-sort-${resolvedProjectId}`

  const hasUrlSort = FILTER_KEYS.some(k => searchParams.has(k))
  const sessionSort = useMemo(
    () => new URLSearchParams(hasUrlSort ? '' : (sessionStorage.getItem(storageKey) ?? '')),
    [hasUrlSort, storageKey],
  )

  const sortByParam = searchParams.get('sortBy') ?? sessionSort.get('sortBy')
  const sortBy: ContextSourceSortColumn =
    sortByParam && sortByParam in CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS ? (sortByParam as ContextSourceSortColumn) : 'createdAt'
  const sortDirParam = searchParams.get('sort') ?? sessionSort.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS[sortBy]

  const persistSort = useCallback((params: URLSearchParams) => {
    const sortParams = new URLSearchParams()
    FILTER_KEYS.forEach(k => {
      const v = params.get(k)
      if (v) sortParams.set(k, v)
    })
    sessionStorage.setItem(storageKey, sortParams.toString())
  }, [storageKey])

  const handleSortChange = useCallback((column: ContextSourceSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS[column]
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('sortBy', column)
      next.set('sort', nextDirection)
      persistSort(next)
      return next
    }, { replace: true })
    resetPage()
  }, [sortBy, sortDirection, setSearchParams, persistSort, resetPage])

  const handleClearAll = useCallback(() => {
    setSearch('')
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      FILTER_KEYS.forEach(k => next.delete(k))
      persistSort(next)
      return next
    }, { replace: true })
    resetPage()
  }, [setSearchParams, persistSort, resetPage])

  const didRestoreSortRef = useRef(false)
  useEffect(() => {
    if (didRestoreSortRef.current) return
    didRestoreSortRef.current = true
    if (FILTER_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(storageKey)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      savedParams.forEach((value, key) => next.set(key, value))
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams, storageKey])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, resetPage])

  const { data, isLoading, isError, refetch } = useContextSources(resolvedProjectId, paginationParams, {
    search: debouncedSearch || undefined,
    sortBy,
    sortDirection,
  })
  const sources = data?.items ?? []
  const showActionColumn = canDelete || sources.some(s => s.kind === 'File')
  const totalCount = data?.totalCount ?? 0
  const deleteMutation = useDeleteContextSource(resolvedProjectId)
  const downloadMutation = useDownloadContextSource(resolvedProjectId)

  const [filesDialogOpen, setFilesDialogOpen] = useState(false)
  const [reposDialogOpen, setReposDialogOpen] = useState(false)
  const [wikiDialogOpen, setWikiDialogOpen] = useState(false)
  const [sourceToDelete, setSourceToDelete] = useState<ContextSourceListItem | null>(null)
  const [sourceToEdit, setSourceToEdit] = useState<ContextSourceListItem | null>(null)

  const handleConfirmDelete = async () => {
    if (!sourceToDelete) return

    try {
      await deleteMutation.mutateAsync(sourceToDelete.id)
      toast.success('Source deleted')
      setSourceToDelete(null)
    } catch {
      toast.error('Failed to delete source')
    }
  }

  const renderTableRows = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={showActionColumn ? 6 : 5} className="text-center text-muted-foreground" data-testid="context-sources-loading" role="status" aria-live="polite">
            Loading sources...
          </TableCell>
        </TableRow>
      )
    }

    if (isError) {
      return (
        <TableRow>
          <TableCell colSpan={showActionColumn ? 6 : 5} className="text-center text-muted-foreground" role="alert" data-testid="context-sources-error">
            Failed to load sources.{' '}
            <button className="underline" onClick={() => refetch()}>
              Retry
            </button>
          </TableCell>
        </TableRow>
      )
    }

    if (sources.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={showActionColumn ? 6 : 5} className="text-center text-muted-foreground">
            No context sources yet.
          </TableCell>
        </TableRow>
      )
    }

    return sources.map((source) => (
      <TableRow key={source.id}>
        <TableCell>
          <div className="flex items-center gap-2">
            <div className="shrink-0 p-2 text-muted-foreground">
              {source.kind === ContextSourceKinds.Wiki ? <MenuBookIcon className="h-4 w-4 text-accent-soft" /> : <SourceIcon source={source} />}
            </div>
            <span className="min-w-0 flex-1 font-medium" data-testid="context-source-name">{source.kind === ContextSourceKinds.Wiki ? `ADO Wiki: ${source.name}` : source.name}</span>
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground">{source.description ?? '-'}</TableCell>
        <TableCell>
          <div className="flex items-center">
            <Badge variant="secondary">{source.sourceType}</Badge>
          </div>
        </TableCell>
        <TableCell>{formatDateTime(source.createdAt)}</TableCell>
        <TableCell>
          <div className="flex flex-col" data-testid="stacked-identity">
            <span className="font-medium">{source.uploadedByName ?? 'Unknown'}</span>
            <span className="text-xs text-muted-foreground">{source.uploadedByEmail ?? '-'}</span>
          </div>
        </TableCell>
        {showActionColumn && (
          <TableCell>
            <div className="flex gap-1">
              {canEdit && source.kind === 'File' && isMarkdownContextFile(source.name) && (
                (source.sizeBytes ?? 0) > MAX_INLINE_EDITABLE_CONTENT_SIZE_BYTES ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled
                            aria-label={`Edit context source ${source.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Too large to edit inline (over {MAX_INLINE_EDITABLE_CONTENT_SIZE_BYTES / 1024 / 1024} MB)
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="cursor-pointer"
                    onClick={() => setSourceToEdit(source)}
                    aria-label={`Edit context source ${source.name}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )
              )}
              {source.kind === 'File' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="cursor-pointer"
                  onClick={() => downloadMutation.mutate(source.id)}
                  disabled={downloadMutation.isPending && downloadMutation.variables === source.id}
                  aria-label={`Download context source ${source.name}`}
                >
                  <Download className="h-4 w-4 text-action-icon" />
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="cursor-pointer"
                  onClick={() => setSourceToDelete(source)}
                  disabled={deleteMutation.isPending}
                  aria-label={`Delete context source ${source.name}`}
                >
                  <Trash2 className="h-4 w-4 text-action-icon" />
                </Button>
              )}
            </div>
          </TableCell>
        )}
      </TableRow>
    ))
  }

  return (
    <div className="space-y-6">
      <ContextHeader
        projectId={resolvedProjectId}
        setFilesDialogOpen={setFilesDialogOpen}
        setReposDialogOpen={setReposDialogOpen}
        setWikiDialogOpen={setWikiDialogOpen}
      />

      <ContextSourcesFilters
        search={search}
        onSearchChange={setSearch}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onClearAll={handleClearAll}
      />

      <div className="rounded-md border" data-testid="context-sources-table">
        <Table aria-label="Context sources">
          <TableHeader>
            <TableRow>
              <SortableTableHead
                column="name"
                label="Name"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
                className="w-[260px]"
              />
              <TableHead>Description</TableHead>
              <SortableTableHead
                column="sourceType"
                label="Source Type"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
                className="w-[135px]"
              />
              <SortableTableHead
                column="createdAt"
                label="Date & Time"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
                className="w-[180px]"
              />
              <SortableTableHead
                column="uploadedBy"
                label="Uploaded By"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={CONTEXT_SOURCE_DEFAULT_SORT_DIRECTIONS}
                onSortChange={handleSortChange}
                className="w-[160px]"
              />
              {showActionColumn && <TableHead className="w-[130px]">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {renderTableRows()}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {canCreate && (
        <>
          <FilesUploadDialog
            open={filesDialogOpen}
            onOpenChange={setFilesDialogOpen}
            projectId={resolvedProjectId}
          />
          <RepositoriesUploadDialog
            open={reposDialogOpen}
            onOpenChange={setReposDialogOpen}
            projectId={resolvedProjectId}
          />
          <WikiPagesUploadDialog
            open={wikiDialogOpen}
            onOpenChange={setWikiDialogOpen}
            projectId={resolvedProjectId}
          />
        </>
      )}

      <EditContextSourceDialog
        projectId={resolvedProjectId}
        source={sourceToEdit}
        open={!!sourceToEdit}
        onOpenChange={(open) => { if (!open) setSourceToEdit(null) }}
      />

      {canDelete && (
        <Dialog open={!!sourceToDelete} onOpenChange={(open) => { if (!open) setSourceToDelete(null) }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Context?</DialogTitle>
            </DialogHeader>
            <DialogDescription>
              Are you sure you want to delete the "<strong className="text-foreground">{sourceToDelete?.name}</strong>" context source? This action cannot be undone and the context source will be permanently removed from the list.
            </DialogDescription>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setSourceToDelete(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Context
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
